import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
function useRowInView() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return {
    ref,
    inView
  };
}
const overlay = {
  background: 'linear-gradient(to top, rgba(61,50,41,0.14) 0%, rgba(245,237,228,0.04) 100%)'
};
const hoverOverlay = {
  background: 'linear-gradient(to top, rgba(184,133,108,0.18) 0%, rgba(245,237,228,0.06) 100%)'
};
function GalleryImage({
  src,
  alt,
  className = ''
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  return <div className={`relative rounded-2xl overflow-hidden group ${className}`} data-id="element-145">
      <img src={src} alt={alt} className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]" data-id="element-146" />
      <div className="absolute inset-0 pointer-events-none" style={overlay} data-id="element-147" />
      <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={hoverOverlay} data-id="element-148" />
    </div>;
}
export function Gallery() {
  const headerRow = useRowInView();
  const row1 = useRowInView();
  const row2 = useRowInView();
  const row3 = useRowInView();
  const row4 = useRowInView();
  const row5 = useRowInView();
  const row6 = useRowInView();
  return <section id="gallery" className="w-full bg-warm-white py-24 md:py-32 px-6 lg:px-8" data-id="element-149">
      <div className="max-w-6xl mx-auto" data-id="element-150">
        {/* Header */}
        <motion.div ref={headerRow.ref} className="text-center mb-14 md:mb-20" initial={{
        opacity: 0,
        y: 30
      }} animate={headerRow.inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} data-id="element-151">
          <p className="text-sm font-body font-medium tracking-[0.25em] uppercase text-sage mb-4" data-id="element-152">
            A Glimpse Inside
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-bark" data-id="element-153">
            Our Space
          </h2>
        </motion.div>

        <div className="space-y-4 md:space-y-6" data-id="element-154">
          {/* ── ROW 1: Night jacuzzi — full-width cinematic hero ── */}
          <motion.div ref={row1.ref} initial={{
          opacity: 0,
          y: 30
        }} animate={row1.inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7
        }} data-id="element-155">
            <GalleryImage src="https://cdn.magicpatterns.com/uploads/xk1BUKjQbkpYLMKFVDjgJF/ZenB-18.jpg" alt="Jacuzzi glowing with purple lights under fairy-lit rustic wooden structure at night" className="w-full aspect-[2/1] md:aspect-[21/9]" data-id="element-156" />
          </motion.div>

          {/* ── ROW 2: Barn wide (60%) + Sauna vertical (40%) ── */}
          <motion.div ref={row2.ref} initial={{
          opacity: 0,
          y: 30
        }} animate={row2.inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7
        }} className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-4 md:gap-6" data-id="element-157">
            <GalleryImage src="https://cdn.magicpatterns.com/uploads/6a9FsmW1V2tVBV1BZcGR8d/ZenB-3-1024x768.jpg" alt="Zen Barn Spa outdoor area with hot tub, barrel tub, and the Saxon barn building" className="aspect-[4/3] md:aspect-auto md:h-[480px]" data-id="element-158" />
            <GalleryImage src="https://cdn.magicpatterns.com/uploads/xpdZHu9jJxqmRxS3nL223T/ZenB-16-768x1024.jpg" alt="View through the glass window of the outdoor Finnish sauna with hills beyond" className="aspect-[3/4] md:aspect-auto md:h-[480px]" data-id="element-159" />
          </motion.div>

          {/* ── ROW 3: Aerial vertical (40%) + Deck overview (60%) ── */}
          <motion.div ref={row3.ref} initial={{
          opacity: 0,
          y: 30
        }} animate={row3.inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7
        }} className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-4 md:gap-6" data-id="element-160">
            <GalleryImage src="https://cdn.magicpatterns.com/uploads/45Q1eydcQRsCM3sMXz2Q6f/ZenB-12-768x1024.jpg" alt="Aerial view of the Zen Barn Spa property nestled among Saxon village houses" className="aspect-[3/4] md:aspect-auto md:h-[480px]" data-id="element-161" />
            <GalleryImage src="https://cdn.magicpatterns.com/uploads/fmr3ZwABkXbNJHcxgupGn3/ZenB-1-1024x768.jpg" alt="Outdoor deck with sun loungers, jade mats, and hot tub surrounded by greenery" className="aspect-[4/3] md:aspect-auto md:h-[480px]" data-id="element-162" />
          </motion.div>

          {/* ── ROW 4: Guests in robes — full-width cinematic ── */}
          <motion.div ref={row4.ref} initial={{
          opacity: 0,
          y: 30
        }} animate={row4.inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7
        }} data-id="element-163">
            <GalleryImage src="https://cdn.magicpatterns.com/uploads/37zuM6WeZ6jGo29LCDuCPg/ZenB-7-1024x768.jpg" alt="Guests in white robes relaxing on the outdoor deck at Zen Barn Spa" className="w-full aspect-[2/1] md:aspect-[21/9]" data-id="element-164" />
          </motion.div>

          {/* ── ROW 5: Trio — treatment + terrace + sauna exterior ── */}
          <motion.div ref={row5.ref} initial={{
          opacity: 0,
          y: 30
        }} animate={row5.inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7
        }} className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6" data-id="element-165">
            <GalleryImage src="https://cdn.magicpatterns.com/uploads/bs8vY7wam1anLvLpCS4D6j/ZenB-5-1024x768.jpg" alt="Therapist working with guests on jade mats on the outdoor deck" className="aspect-[4/3] sm:aspect-auto sm:h-[320px]" data-id="element-166" />
            <GalleryImage src="https://cdn.magicpatterns.com/uploads/rGyRdtJRZJxACqc1RnHy2H/ZenB-15-1024x768.jpg" alt="Terrace view overlooking village rooftops and green Transylvanian hills" className="aspect-[4/3] sm:aspect-auto sm:h-[320px]" data-id="element-167" />
            <GalleryImage src="https://cdn.magicpatterns.com/uploads/7ty3RG1cwASbvTJADWbXDu/ZenB-14-1024x768.jpg" alt="Happy guests outside the outdoor sauna cabin surrounded by lush greenery" className="aspect-[4/3] sm:aspect-auto sm:h-[320px]" data-id="element-168" />
          </motion.div>

          {/* ── ROW 6: Hot tub vertical (40%) + Treatment barn (60%) ── */}
          <motion.div ref={row6.ref} initial={{
          opacity: 0,
          y: 30
        }} animate={row6.inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7
        }} className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-4 md:gap-6" data-id="element-169">
            <GalleryImage src="https://cdn.magicpatterns.com/uploads/p9YcSgscpWiAy8cFFjoGZa/ZenB-10-768x1024.jpg" alt="Guests in the hot tub with rustic wooden staircase and Buddha statue" className="aspect-[3/4] md:aspect-auto md:h-[480px]" data-id="element-170" />
            <GalleryImage src="https://cdn.magicpatterns.com/uploads/rGWD5tMeJgpxyqG7kUaGDy/ZenB-6-1024x768.jpg" alt="Guests relaxing on jade mats with the Saxon barn architecture visible" className="aspect-[4/3] md:aspect-auto md:h-[480px]" data-id="element-171" />
          </motion.div>
        </div>
      </div>
    </section>;
}